import React from "react";
import { useTranslation } from "next-i18next";
import styled from "@emotion/styled";
import Popper from "@mui/material/Popper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { useRouter } from "next/router";
import { B2 } from "@/modules/common/fonts";
import { useLocale } from "@/modules/provider/LocaleProvider";

const LanguageSelector = () => {
  const router = useRouter();
  const { t } = useTranslation();
  const locale = useLocale();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popper" : undefined;
  const handleClickAway = () => {
    setAnchorEl(null);
  };

  const changeLang = (lang: string) => {
    if (locale === lang) {
      handleClickAway();
      return;
    }

    document.cookie = `NEXT_LOCALE=${lang}; path=/;`;
    router.replace(`/`, undefined, { locale: lang });
  };

  const LOCALE_LIST = [
    {
      locale: "en",
      label: "option_en",
    },
    {
      locale: "ko",
      label: "option_ko",
    },
    {
      locale: "ja",
      label: "option_ja",
    },
  ];

  return (
    <Wrapper>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div>
          <SelectWrapper
            className="select__lang__icon"
            open={open}
            onClick={handleClick}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0 8C0 5.87827 0.842855 3.84344 2.34315 2.34315C3.84344 0.842855 5.87827 0 8 0C10.1217 0 12.1566 0.842855 13.6569 2.34315C15.1571 3.84344 16 5.87827 16 8C16 10.1217 15.1571 12.1566 13.6569 13.6569C12.1566 15.1571 10.1217 16 8 16C5.87827 16 3.84344 15.1571 2.34315 13.6569C0.842855 12.1566 0 10.1217 0 8ZM7.5 1.077C6.83 1.281 6.165 1.897 5.613 2.932C5.47 3.2 5.337 3.492 5.218 3.804C5.923 3.961 6.69 4.061 7.5 4.091V1.077ZM4.249 3.539C4.391 3.155 4.553 2.795 4.73 2.461C4.90291 2.13406 5.10259 1.82199 5.327 1.528C4.47567 1.8808 3.70225 2.398 3.051 3.05C3.413 3.234 3.814 3.399 4.249 3.54V3.539ZM3.509 7.5C3.545 6.43 3.697 5.413 3.945 4.492C3.4037 4.31889 2.87976 4.09559 2.38 3.825C1.58354 4.89435 1.11082 6.16987 1.018 7.5H3.508H3.509ZM4.909 4.759C4.67325 5.65477 4.53907 6.57422 4.509 7.5H7.5V5.091C6.59 5.061 5.717 4.946 4.909 4.759ZM8.5 5.09V7.5H11.49C11.4603 6.57426 11.3264 5.6548 11.091 4.759C10.283 4.946 9.41 5.06 8.5 5.091V5.09ZM4.51 8.5C4.545 9.487 4.686 10.414 4.909 11.241C5.76006 11.0479 6.62772 10.937 7.5 10.91V8.5H4.51ZM8.5 8.5V10.909C9.41 10.939 10.283 11.054 11.091 11.241C11.314 10.414 11.455 9.487 11.491 8.5H8.5ZM5.218 12.196C5.338 12.508 5.47 12.8 5.613 13.068C6.165 14.103 6.831 14.718 7.5 14.923V11.91C6.69 11.94 5.923 12.04 5.218 12.197V12.196ZM5.328 14.472C5.10323 14.1781 4.90321 13.866 4.73 13.539C4.54582 13.1904 4.38513 12.83 4.249 12.46C3.83763 12.5922 3.4371 12.756 3.051 12.95C3.70225 13.602 4.47567 14.1192 5.327 14.472H5.328ZM3.945 11.508C3.68538 10.5252 3.53876 9.51603 3.508 8.5H1.018C1.11077 9.83014 1.5835 11.1057 2.38 12.175C2.85 11.917 3.375 11.693 3.945 11.508ZM10.673 14.472C11.5239 14.1194 12.297 13.6026 12.948 12.951C12.5622 12.7571 12.162 12.5932 11.751 12.461C11.6148 12.8306 11.4541 13.1908 11.27 13.539C11.0971 13.866 10.8975 14.178 10.673 14.472ZM8.5 11.909V14.923C9.17 14.719 9.835 14.103 10.387 13.068C10.53 12.8 10.663 12.508 10.782 12.196C10.0316 12.0315 9.26778 11.9357 8.5 11.91V11.909ZM12.055 11.508C12.625 11.693 13.15 11.917 13.62 12.175C14.4165 11.1057 14.8892 9.83014 14.982 8.5H12.492C12.4612 9.51603 12.3146 10.5252 12.055 11.508ZM14.982 7.5C14.8892 6.16986 14.4165 4.89433 13.62 3.825C13.15 4.083 12.625 4.307 12.055 4.492C12.303 5.412 12.455 6.43 12.492 7.5H14.982ZM11.27 2.461C11.447 2.795 11.609 3.155 11.752 3.539C12.1627 3.40675 12.5626 3.24292 12.948 3.049C12.2969 2.39782 11.5238 1.88132 10.673 1.529C10.891 1.812 11.091 2.126 11.27 2.461ZM10.782 3.804C10.6684 3.50549 10.5365 3.21427 10.387 2.932C9.835 1.897 9.17 1.282 8.5 1.077V4.09C9.31 4.06 10.077 3.96 10.782 3.803V3.804Z"
                fill="#616161"
              />
            </svg>
          </SelectWrapper>
          <Popper
            id={id}
            open={open}
            anchorEl={anchorEl}
            placement="bottom-end"
            style={{ zIndex: 9999 }}
            popperOptions={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 8],
                  },
                },
              ],
            }}
          >
            <SelectList>
              {LOCALE_LIST.map((locales) => {
                return (
                  <ListItem
                    key={locales.locale}
                    pick={locale === locales.locale}
                    onClick={() => {
                      changeLang(locales.locale);
                    }}
                  >
                    <ListText>{t(locales.label)}</ListText>
                  </ListItem>
                );
              })}
            </SelectList>
          </Popper>
        </div>
      </ClickAwayListener>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0 16px;
`;

const SelectWrapper = styled.div<{ open: boolean }>`
  position: relative;
  cursor: pointer;
  font-size: 0;
  margin-left: 8px;
  & svg path {
    fill: var(--gray080);
  }
  &:active svg path {
    fill: var(--blue060);
  }
  ${(props) =>
    props.open &&
    `
    & svg path {
      fill: var(--blue060) !important;
    }`}
`;

const SelectList = styled.div`
  box-shadow: var(--dropdownMenu_elevation_gray);
  border-radius: 8px;
  max-height: 250px;
  overflow-y: auto;
  background: #fff;
  padding: 8px 0;
`;

const ListItem = styled.div<{ pick: boolean }>`
  cursor: pointer;
  min-width: 92px;
  padding: 7px 14px;
  color: ${(props) => (props.pick ? "var(--blue060)" : "var(--black)")};
  &:hover {
    background-color: var(--dropdownMenu_hover_base_gray);
  }
`;

const ListText = styled(B2)`
  font-weight: 600;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

export default LanguageSelector;
